import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMessageLabels, setMessageLabel } from "@/lib/api/mailLabels";
import type { MailColorLabel, MailMessageLabel } from "@/lib/mail/messageLabels";
import type { MailMessageSummary } from "@/lib/types/mail";

const labelsCache = new Map<string, Map<number, MailMessageLabel>>();

function cacheKey(accountId: number, folder: string) {
  return `${accountId}:${folder}`;
}

/** Cache module-level labels (hors React). */
export function clearMessageLabelsRuntimeCache(accountId?: number): void {
  if (accountId == null) {
    labelsCache.clear();
    return;
  }
  for (const key of [...labelsCache.keys()]) {
    if (key.startsWith(`${accountId}:`)) {
      labelsCache.delete(key);
    }
  }
}

export function useMessageLabels(accountId?: number, folder = "INBOX") {
  const [labels, setLabels] = useState<Map<number, MailMessageLabel>>(() => new Map());
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (accountId == null) {
      setLabels(new Map());
      return;
    }

    const key = cacheKey(accountId, folder);
    const cached = labelsCache.get(key);
    if (cached) {
      setLabels(new Map(cached));
    }

    setLoading(true);
    const res = await fetchMessageLabels({ accountId, folder });
    setLoading(false);

    if (!res.ok) return;

    const next = new Map<number, MailMessageLabel>();
    for (const [uidKey, entry] of Object.entries(res.data.labels ?? {})) {
      const uid = Number(uidKey);
      if (uid < 1) continue;
      next.set(uid, {
        favorite: !!entry.favorite,
        color: entry.color ?? null,
      });
    }
    labelsCache.set(key, next);
    setLabels(next);
  }, [accountId, folder]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const applyToMessages = useCallback(
    (messages: MailMessageSummary[]) =>
      messages.map((m) => {
        const label = labels.get(m.uid);
        if (!label) return m;
        return {
          ...m,
          favorite: label.favorite,
          colorLabel: label.color ?? undefined,
        };
      }),
    [labels]
  );

  const updateLabel = useCallback(
    async (uid: number, patch: { favorite?: boolean; color?: MailColorLabel | null }) => {
      if (accountId == null || uid < 1) return;

      setLabels((prev) => {
        const next = new Map(prev);
        const current = next.get(uid) ?? { favorite: false, color: null };
        next.set(uid, {
          favorite: patch.favorite ?? current.favorite,
          color: patch.color !== undefined ? patch.color : current.color,
        });
        labelsCache.set(cacheKey(accountId, folder), next);
        return next;
      });

      void setMessageLabel({
        accountId,
        folder,
        uid,
        ...patch,
      });
    },
    [accountId, folder]
  );

  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  return { labels, loading, reload, applyToMessages, updateLabel };
}
